import { useCallback, useEffect, useState } from 'react';
import { ADJUSTMENTS, defaultAdjustmentState, type AdjustmentId } from '../../adjustments';
import type { AdjustmentState, DocumentMeta } from '../../core/types';
import { getEngine } from '../../engine/engine';
import { renderAdjustmentPreview } from '../../engine/ops';
import { applyAdjustment } from '../../state/actions';
import { liveRender } from '../../state/renderState';
import { useAppStore } from '../../state/store';
import { AdjustmentEditor } from '../components/AdjustmentEditor';
import { Checkbox, Modal } from '../components/controls';
import { GradientEditor } from './GradientEditor';

/**
 * Live preview reuses the same shader as the final apply: the result is rendered
 * into a scratch texture that temporarily stands in for the layer, so nothing is
 * written to the layer until the user confirms.
 */
export function AdjustmentDialog({
  id,
  doc,
  open,
  onClose,
}: {
  id: AdjustmentId;
  doc: DocumentMeta;
  open: boolean;
  onClose: () => void;
}) {
  const definition = ADJUSTMENTS[id];
  const [state, setState] = useState<AdjustmentState>(() => defaultAdjustmentState(id));
  const [preview, setPreview] = useState(true);
  const [editingGradient, setEditingGradient] = useState(false);
  const gradientPresets = useAppStore((store) => store.gradient.presets);
  const engine = getEngine();

  const clearPreview = useCallback(() => {
    if (doc.activeLayerId) liveRender.overrides.delete(doc.activeLayerId);
    engine.invalidate();
  }, [doc.activeLayerId, engine]);

  useEffect(() => {
    const layerId = doc.activeLayerId;
    if (!open || !layerId || !engine.available) return;
    if (!preview) {
      clearPreview();
      return;
    }
    const texture = renderAdjustmentPreview(engine, doc, layerId, state);
    liveRender.overrides.set(layerId, texture);
    engine.invalidate();
  }, [open, preview, state, doc, engine, clearPreview]);

  useEffect(() => clearPreview, [clearPreview]);

  const close = () => {
    clearPreview();
    onClose();
  };

  const apply = () => {
    clearPreview();
    applyAdjustment(state);
    onClose();
  };

  return (
    <>
      <Modal
        open={open}
        title={definition.label}
        onClose={close}
        footer={
          <>
            <button
              type="button"
              className="button"
              onClick={() => setState(defaultAdjustmentState(id))}
            >
              Reset
            </button>
            <button type="button" className="button" onClick={close}>
              Cancel
            </button>
            <button type="button" className="button button--primary" onClick={apply}>
              Apply
            </button>
          </>
        }
      >
        <AdjustmentEditor
          state={state}
          onChange={setState}
          onEditGradient={() => setEditingGradient(true)}
        />
        <Checkbox label="Preview" checked={preview} onChange={setPreview} />
        {doc.hasSelection && (
          <p className="dialog__note">The adjustment is limited to the current selection.</p>
        )}
      </Modal>

      {editingGradient && state.gradient && (
        <GradientEditor
          gradient={state.gradient}
          presets={gradientPresets}
          onChange={(gradient) => setState((current) => ({ ...current, gradient }))}
          onClose={() => setEditingGradient(false)}
        />
      )}
    </>
  );
}
